import { useMemo } from 'react';

import clsx from 'clsx';

import getStockStateString from '~/features/products/utils/stockState/getStockStateString';
import { createPolymorphicComponent } from '~/helpers/typescript-helpers';
import { ILineItem } from '~/lib/data-contract';
import { AspectRatio } from '~/shared/components/AspectRatio/AspectRatio';
import { ImageMedia } from '~/shared/components/ImageMedia/ImageMedia';
import { useTranslation } from '~/shared/utils/translation/hooks/useTranslation';

import styles from './lineItem.module.css';

import { LineItemPrice } from '../LineItemPrice/LineItemPrice';

export const imageSizes = '96px';

export type LineItemProps = Pick<ILineItem, 'variation' | 'quantity' | 'linePrice' | 'discountedLinePrice' | 'discount'> & {
	labelType: 'brand' | 'series';
	className?: string;
	imageSizes?: string;
	isUpsellButton?: React.ReactNode;
};

export const LineItem = createPolymorphicComponent<'li', LineItemProps>(
	(
		{
			as: ComponentType = 'li',
			variation,
			quantity,
			linePrice,
			discountedLinePrice,
			discount,
			labelType,
			className,
			imageSizes: imageSizesProp = imageSizes,
			isUpsellButton,
			// Filter out ILineItem props that shouldn't bleed to DOM when spreading {...line}
			giftWrappingSelected: _giftWrappingSelected,
			isGift: _isGift,
			itemReturned: _itemReturned,
			sustainablePackagingSelected: _sustainablePackagingSelected,
			sustainablePackagingEnabled: _sustainablePackagingEnabled,
			giftWrappingPrice: _giftWrappingPrice,
			unitPrice: _unitPrice,
			giftWrappingEnabled: _giftWrappingEnabled,
			greetingCardText: _greetingCardText,
			sustainablePackagingPrice: _sustainablePackagingPrice,
			id: _id,
			...rest
		},
		ref,
	) => {
		const { name, image, color, designCollection, brand } = variation;
		const { colorName } = color || {};

		const { translate } = useTranslation();

		const constructedName = useMemo(() => {
			const bits = [name];

			if (colorName) {
				bits.push(colorName);
			}

			return bits.join(', ');
		}, [variation]);

		let label: string | undefined = undefined;

		if (labelType === 'brand' && brand?.name) {
			label = brand.name;
		}

		if (labelType === 'series' && designCollection?.name) {
			label = designCollection.name;
		}

		const stockStateString = getStockStateString(variation.stockAvailable, variation.stockState);

		return (
			<ComponentType
				className={clsx(styles.root, className)}
				{...rest}
				ref={ref}
			>
				<div
					className={styles.thumbnail}
					data-line-item-thumbnail
				>
					<AspectRatio ratio={70 / 85}>
						{image ? (
							<ImageMedia
								sizes={imageSizesProp}
								image={image}
								fill
								objectFit="contain"
							/>
						) : null}
					</AspectRatio>
				</div>
				<div>
					{label ? (
						<h3
							className={styles.titleBold}
							data-line-item-title
						>
							{label}
						</h3>
					) : null}
					<p
						className={styles.title}
						data-line-item-title
					>
						{quantity} x {constructedName}
					</p>
					{stockStateString === 'preorder' ? <span className={styles.preOrder}>{translate('line-item.preorder')}</span> : null}
				</div>
				<div className={isUpsellButton ? styles.priceUpsell : styles.price}>
					<LineItemPrice
						linePrice={quantity ? linePrice : 0}
						discountedLinePrice={discountedLinePrice}
						discount={discount}
					/>
					{isUpsellButton}
				</div>
			</ComponentType>
		);
	},
);
